"use client";

import lightModeLogo from "@/public/artefact_dark.svg";
import { Flex, Text } from "@radix-ui/themes";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/app/components/ui/card";

import { useRouter } from "next/navigation";
import { CredentialsSignInForm } from "./SignIn";
import CredentialsSignUpForm from "./SignUp";
import { cn } from "@/lib/utils";
import GoogleSignInButton from "./GoogleSignInButton";
import { useDynamicConfig } from "@statsig/react-bindings";
import { TenantInfoKey } from "@/lib/enum/statsig/tenantInfoKey";
import SignInLogo from "./SignInLogo";

interface Props {
  callbackURL?: string | undefined;
  loginPage?: boolean;
  renderBanner?: React.ReactNode | undefined;
  overrideClickForgot?: any;
  onComplete?: any;
}

interface SignInForm {
  email: string;
  password: string;
}

const SignInForm = ({
  callbackURL = "/",
  loginPage = true,
  renderBanner,
  overrideClickForgot,
  onComplete,
}: Props) => {
  const config = useDynamicConfig("tenant_info");
  const [loggingIn, setLoggingIn] = useState(false);
  const [provider, setProvider] = useState<null | "google" | "credentials">(
    null
  );
  const [createAccount, setCreateAccount] = useState(false);
  const [forgot, setForgot] = useState(false);
  const router = useRouter();

  const formProps = {
    loggingIn,
    setLoggingIn,
    callbackURL,
    router,
    onComplete,
  };

  const showSignIn = !createAccount && !forgot;

  return (
    <Flex
      justify="center"
      // align={loginPage ? "start" : "center"}
      align="start"
      className={"w-full p-1"}
    >
      <div
        className={cn(
          "max-md:w-[92%] md:w-[490px] h-[95%] max-sm:h-[90%]",
          loginPage ? "mt-6 pb-6" : "mt-0",
          "flex flex-col md:justify-between max-md:justify-start"
        )}
      >
        <Flex align="center" direction="column" className="space-y-5">
          {loginPage && (
            <Flex direction="column">
              <Flex className="w-full" justify="center">
                <Link href="/">
                  <SignInLogo />
                </Link>
              </Flex>

              {!forgot &&
                <Flex className="w-full" justify="center">
                  <h3 className="text-center">{createAccount ? "Sign up for" : "Sign in to"} {config.get(TenantInfoKey.TenantName) as string}</h3>
                </Flex>
              }
            </Flex>
          )}
          {(!loginPage && renderBanner) && (
              <Flex
                className="w-full my-2"
                justify={"center"}
                align={"center"}
              >
                <SignInLogo />
              </Flex>
            )}

          <GoogleSignInButton
            showSignIn={showSignIn}
            loggingIn={loggingIn}
            provider={provider}
            setLoggingIn={setLoggingIn}
            setProvider={setProvider}
            callbackURL={callbackURL}
            loginPage={loginPage}
            onComplete={onComplete}
          />

          {createAccount ? (
            <CredentialsSignUpForm {...formProps} />
          ) : (
            <>
              <Card className="w-full mb-2">
                <CardHeader className="pb-4">
                  <CardTitle className="text-center text-lg font-medium">
                    {forgot ? "Forgot Password?" : "Sign in with Email & Password"}
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-500">
                    {forgot
                      ? "Please enter your email to receive password reset instructions."
                      : "Please use this option if you originally signed up with an email and password."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <CredentialsSignInForm
                    {...formProps}
                    forgot={forgot}
                    setForgot={setForgot}
                  onClickForgot={overrideClickForgot}
                  onClickSignUp={() => {
                    if (!loggingIn) setCreateAccount(true);
                  }}
                  />
                </CardContent>
              </Card>
              {!forgot &&
                <Flex className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm rounded px-4 py-3 mt-1">
                  <span>
                    <span className="font-semibold">Important: </span>
                    You must use the same sign-in method you used when you first created your account.
                  </span>
                </Flex>
              }
            </>
          )}
        </Flex>

        <Flex
          justify="center"
          className={
            loginPage
              ? "mt-16 mb-6"
              : "mt-6 mb-2"
          }
        >
          {createAccount ? (
            <Text className="text-sm text-slate-500">
              Already have an account?{" "}
              <span
                onClick={() => {
                  if (!loggingIn) setCreateAccount(false);
                }}
                className="font-medium text-slate-900 cursor-pointer"
              >
                Sign in
              </span>
            </Text>
          ) : null}
        </Flex>
      </div>
    </Flex>
  );
};

export default SignInForm;